/*
 * Copyright 2022-2026 NXGN Management, LLC. All Rights Reserved.
 */

import { Component, Input, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { AuthService, MerchantService, NotificationService } from "../../../services";
import { MerchantDetailsParentComponent } from "../merchant-details-parent.component";
import { InstaMedAccountDetails, Merchant } from "../../../models";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { PATTERN_CONSTANTS } from "../../../utils/data.rest.constants";
import { minSelected } from "../../../validators/MPCustomValidator";

@Component({
  selector: "app-instamed-details",
  templateUrl: "./instamed-details.component.html"
})
export class InstamedDetailsComponent extends MerchantDetailsParentComponent implements OnInit {
  instamedFormGroup!: FormGroup;
  editable = false;
  buttonName = "Update InstaMed Details";
  actionInProgress = false;
  @Input("loaded") loaded = false;
  @Input("merchant") merchant!: Merchant;

  constructor(
    router: Router,
    activatedRoute: ActivatedRoute,
    merchantService: MerchantService,
    notificationService: NotificationService,
    authService: AuthService,
    private fb: FormBuilder
  ) {
    super(router, activatedRoute, merchantService, notificationService, authService);
  }

  ngOnInit(): void {
    this.createForm();
  }

  createForm() {
    this.instamedFormGroup = this.fb.group({
      merchantId: [this.merchant.accountDetails.instaMedAccountDetails.merchantId, Validators.required],
      clientId: [this.merchant.accountDetails.instaMedAccountDetails.clientId, Validators.maxLength(255)],
      clientSecret: [this.merchant.accountDetails.instaMedAccountDetails.clientSecret, Validators.maxLength(255)],
      storeId: [
        this.merchant.accountDetails.instaMedAccountDetails.storeId,
        [Validators.required, Validators.pattern(PATTERN_CONSTANTS.NUMBER_4), Validators.maxLength(4)]
      ],
      accountId: [this.merchant.accountDetails.instaMedAccountDetails.accountId],
      terminalInfo: this.fb.group(
        {
          patientPortal: [
            this.merchant.accountDetails.instaMedAccountDetails?.instaMedTerminalIDList?.PATIENT_PORTAL,
            [Validators.pattern(PATTERN_CONSTANTS.NUMBER_4), Validators.maxLength(4)]
          ],
          virtualVisits: [
            this.merchant.accountDetails.instaMedAccountDetails?.instaMedTerminalIDList?.VIRTUAL_VISITS,
            [Validators.pattern(PATTERN_CONSTANTS.NUMBER_4), Validators.maxLength(4)]
          ],
          engage: [
            this.merchant.accountDetails.instaMedAccountDetails?.instaMedTerminalIDList?.ENGAGE,
            [Validators.pattern(PATTERN_CONSTANTS.NUMBER_4), Validators.maxLength(4)]
          ]
        },
        { validators: minSelected(1) }
      )
    });
  }

  setEditable(value: boolean): void {
    this.editable = value;
  }

  receiveEditEvent(value: boolean) {
    this.editable = value;
    this.createForm();
  }

  updateInstamedDetails() {
    if (this.instamedFormGroup.valid) {
      this.actionInProgress = true;
      const instaMedAccountDetails: InstaMedAccountDetails = JSON.parse(
        JSON.stringify(this.merchant.accountDetails.instaMedAccountDetails)
      );
      instaMedAccountDetails.merchantId = this.instamedFormGroup.controls.merchantId.value;
      instaMedAccountDetails.clientId = this.instamedFormGroup.controls.clientId.value;
      instaMedAccountDetails.clientSecret = this.instamedFormGroup.controls.clientSecret.value;
      instaMedAccountDetails.storeId = this.instamedFormGroup.controls.storeId.value;
      instaMedAccountDetails.accountId = this.instamedFormGroup.controls.accountId.value;
      instaMedAccountDetails.instaMedTerminalIDList.PATIENT_PORTAL = this.terminalIdGroup.controls.patientPortal.value;
      instaMedAccountDetails.instaMedTerminalIDList.VIRTUAL_VISITS = this.terminalIdGroup.controls.virtualVisits.value;
      instaMedAccountDetails.instaMedTerminalIDList.ENGAGE = this.terminalIdGroup.controls.engage.value;
      this.merchantService.updateInstamedAccountDetails(this.merchant.id, instaMedAccountDetails).subscribe(
        (instaMedAccountDetails) => {
          this.merchant.accountDetails.instaMedAccountDetails = instaMedAccountDetails;
          this.editable = false;
          this.actionInProgress = false;
        },
        (error) => {
          this.notificationService.error(error.error);
          this.actionInProgress = false;
        }
      );
    } else {
      this.instamedFormGroup.markAllAsTouched();
    }
  }

  get terminalIdGroup(): FormGroup {
    return this.instamedFormGroup.controls.terminalInfo as FormGroup;
  }
}
